import { whenText } from './claude-fleet.live'
import type { FleetRunner, FleetRunnerHealth, FleetRunnerLimits, FleetRunnerUsageWindow } from './claude-fleet.types'

/** Under an hour left on AWS SSO or the VPN is a warning: the laptop stops taking work once either ends. */
const SSO_WARN_MS = 60 * 60 * 1000
/** The free disk under which a laptop runner takes nothing (its minDiskGb). */
const DISK_WARN_GB = 15

interface UsageWindowSpec {
  key: string
  label: string
  /** The share at which a laptop on the default reserve stops taking work (100 minus reserve5h 20 or reserve7d 30). */
  warnPct: number
}

const USAGE_5H: UsageWindowSpec = { key: 'usage-5h', label: 'Claude usage (5h)', warnPct: 80 }
const USAGE_7D: UsageWindowSpec = { key: 'usage-7d', label: 'Claude usage (7d)', warnPct: 70 }

export interface FleetRunnerCapacityView {
  /** "2 of 10 · 5h window at 81%", "2 of 2 · fixed". */
  label: string
  /** The count before the first " · ", "2 of 10": the card for a hard gate, so the card never cuts its reason. */
  slots: string
  /** What limits it, after the count ("5h window at 81%"); empty when the line names nothing. */
  reason: string
  /** A hard gate holds it at 0: shown as a warning. */
  gated: boolean
  /** Running and free, for the hover. */
  title: string
}

export interface FleetRunnerHealthItem {
  key: string
  label: string
  value: string
  /** What the sidebar runner card says while this item is its warning, short enough for its width: "SSO ends in 25m". */
  short?: string
  title?: string
  warning?: boolean
}

/** The Capacity column for a laptop that sends its slots; null for an older one, which shows its free slots. */
export function runnerCapacity(runner: Pick<FleetRunner, 'slotsLine' | 'slotsGated' | 'running' | 'freeSlots' | 'health'>): FleetRunnerCapacityView | null {
  if (!runner.slotsLine) return null
  const [slots, ...reason] = runner.slotsLine.split(' · ')
  return {
    label: runner.slotsLine,
    slots,
    reason: reason.join(' · '),
    gated: Boolean(runner.slotsGated),
    title: capacityTitle(runner)
  }
}

function capacityTitle(runner: Pick<FleetRunner, 'running' | 'freeSlots' | 'health'>): string {
  const slots = runner.health?.slots
  // The count before " · " is the slots open now; in use is what the laptop says it is not offering of those.
  const open = slots?.now
  const inUse = typeof open === 'number' ? Math.max(0, open - runner.freeSlots) : runner.running
  const parts = [
    typeof open === 'number' ? `${open} ${open === 1 ? 'slot' : 'slots'} open` : '',
    typeof inUse === 'number' ? `${inUse} in use` : '',
    `${runner.freeSlots} free`,
    slots?.mode === 'pinned' && typeof slots.pinned === 'number' ? `fixed at ${slots.pinned}` : '',
    slots?.mode === 'auto' ? 'sized each minute by Claude usage' : ''
  ]
  return parts.filter(Boolean).join(' · ')
}

/** The drawer's Health section: one item for each value the laptop sent, none for one it did not. */
export function runnerHealthItems(health: FleetRunnerHealth | undefined, now: number): FleetRunnerHealthItem[] {
  if (!health) return []
  const items = [
    ssoItem(health.ssoExpiresAt, now),
    signinItem(health.signinReady),
    vpnItem(health.vpn, health.vpnEndsAt, now),
    diskItem(health.diskFreeGb),
    usageItem(withReserve(USAGE_5H, health.limits?.reserve5hPct), health.usage?.h5, now),
    usageItem(withReserve(USAGE_7D, health.limits?.reserve7dPct), health.usage?.d7, now),
    ...limitItems(health.limits),
    ...unusedItems(health.limits, now)
  ]
  return items.filter((item): item is FleetRunnerHealthItem => item !== null)
}

/** A usage window warns where this runner stops: 100 less the reserve it reports, else the spec's default. */
function withReserve(spec: UsageWindowSpec, reservePct: number | undefined): UsageWindowSpec {
  return typeof reservePct === 'number' ? { ...spec, warnPct: 100 - reservePct } : spec
}

const SOURCE_WORDS: Record<string, string> = {
  yours: "owner's choice",
  fleet: 'fleet default',
  earlier: 'as before',
  lastCall: 'lifted until the 7-day reset'
}

// What is unused when the 7-day window resets is lost; at this share the runner also tells its owner.
const UNUSED_WARN_PCT = 20

/** Where one limit came from, from the runner's "dayShare7d=fleet,reserve7d=yours". */
function sourceOf(limits: FleetRunnerLimits, key: string): string {
  const found = (limits.source ?? '').split(',').map(part => part.split('=')).find(([name]) => name === key)?.[1]
  return found && SOURCE_WORDS[found] ? ` · ${SOURCE_WORDS[found]}` : ''
}

/** The drawer's usage limits: today's share of the 7-day window, the 7-day reserve and why it holds back now. */
function limitItems(limits: FleetRunnerLimits | undefined): FleetRunnerHealthItem[] {
  if (!limits) return []
  const used = Math.round(limits.dayUsedPct ?? 0)
  // 0 means no limit, as the Settings field says, so only a positive number is a limit.
  const dayLimit = typeof limits.dayLimitPct === 'number' && limits.dayLimitPct > 0 ? limits.dayLimitPct : undefined
  const items: FleetRunnerHealthItem[] = [
    {
      key: 'day-share',
      label: "Today's share (7d)",
      value: (dayLimit !== undefined ? `${used}% of ${Math.round(dayLimit)}%` : `${used}% · no daily limit`) + sourceOf(limits, 'dayShare7d'),
      title:
        "The 7-day Claude window fleet work used since midnight New York; the owner's own PRs are not counted. The owner sets their limits on My work → My runner",
      warning: dayLimit !== undefined && (limits.dayUsedPct ?? 0) >= dayLimit
    }
  ]
  if (typeof limits.reserve7dPct === 'number') {
    items.push({
      key: 'reserve-7d',
      label: 'Kept for the owner (7d)',
      value: `${Math.round(limits.reserve7dPct)}%${sourceOf(limits, 'reserve7d')}`,
      title: 'The owner sets this on My work → My runner, for every machine they run on'
    })
  }
  if (limits.held) items.push({ key: 'held', label: 'Holding back', value: limits.held, warning: true })
  return items
}

/** What the 7-day window will leave unused at its reset, and what past windows left: each share is usage paid for
 *  and lost. */
function unusedItems(limits: FleetRunnerLimits | undefined, now: number): FleetRunnerHealthItem[] {
  if (!limits) return []
  const items: FleetRunnerHealthItem[] = []
  if (typeof limits.unusedPct === 'number' && limits.unusedPct > 0) {
    items.push({
      key: 'unused-7d',
      label: 'Goes unused (7d)',
      value:
        `about ${Math.round(limits.unusedPct)}% at the pace so far` +
        (limits.lastCallUntil ? ` · runner using it until ${endsText(limits.lastCallUntil, now)}` : ''),
      title:
        "What is unused when the 7-day window resets is lost. In the window's last day the runner takes extra fleet work to use it, unless its owner turned that off",
      warning: limits.unusedPct >= UNUSED_WARN_PCT
    })
  }
  if (limits.unusedWeeks?.length) {
    items.push({
      key: 'unused-past',
      label: 'Unused at past resets',
      value: [...limits.unusedWeeks]
        .reverse()
        .slice(0, 4)
        .map(week => `${Math.round(week.unusedPct)}% (${dayText(week.endedAt)})`)
        .join(' · '),
      title: "Each figure is the share of that week's 7-day window its last reading left unused, so at most what was lost"
    })
  }
  return items
}

function dayText(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** A 24-hour clock time, "11:56", as the rest of the fleet page shows one. */
function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

/** "in 3h · 15:00", "tomorrow 04:19", or "Expired 5m ago": when a session ends, as the drawer shows it. */
function endsText(endsAt: string, now: number): string {
  const when = whenText(endsAt, now)
  const clock = clockTime(endsAt)
  // A far time already reads "Jan 22 07:34"; only a relative one ("in 3h") gets its clock time added.
  return Date.parse(endsAt) <= now ? `Expired ${when}` : when.includes(clock) ? when : `${when} · ${clock}`
}

/** The runner sends when its SSO session ends once it has learned how long one lasts; nothing before that. */
function ssoItem(expiresAt: string | undefined, now: number): FleetRunnerHealthItem | null {
  if (!expiresAt || Number.isNaN(Date.parse(expiresAt))) return null
  const left = Date.parse(expiresAt) - now
  return {
    key: 'sso',
    label: 'AWS sign-in ends',
    value: endsText(expiresAt, now),
    short: left <= 0 ? 'SSO expired' : `SSO ends ${whenText(expiresAt, now)}`,
    title: new Date(expiresAt).toLocaleString(),
    warning: left < SSO_WARN_MS
  }
}

/** Only a runner that says it has no key yet gets the line: when its SSO ends, no sign-in link can reach its owner. */
function signinItem(ready: boolean | undefined): FleetRunnerHealthItem | null {
  if (ready !== false) return null
  return {
    key: 'signin',
    label: 'Phone sign-in',
    value: 'Not set up yet',
    short: 'Set up sign-in',
    title: 'Sign in to AWS on this machine once (aws sso login), so the runner can send sign-in links to your phone',
    warning: true
  }
}

/** Connected, with when the session ends once the runner read it from OpenVPN Connect's log. */
function vpnItem(vpn: FleetRunnerHealth['vpn'], endsAt: string | undefined, now: number): FleetRunnerHealthItem | null {
  if (!vpn) return null
  if (vpn === 'down') return { key: 'vpn', label: 'VPN', value: 'Dev and staging not reachable', short: 'VPN down', warning: true }
  const ends = endsAt && Date.parse(endsAt) > now ? endsAt : undefined
  if (!ends) return { key: 'vpn', label: 'VPN', value: 'Connected' }
  return {
    key: 'vpn',
    label: 'VPN',
    value: `Connected · ends ${endsText(ends, now)}`,
    short: `VPN ends ${whenText(ends, now)}`,
    title: `${new Date(ends).toLocaleString()}: the VPN session ends then, and asks you to sign in again`,
    warning: Date.parse(ends) - now < SSO_WARN_MS
  }
}

function diskItem(diskFreeGb: number | undefined): FleetRunnerHealthItem | null {
  if (typeof diskFreeGb !== 'number') return null
  return {
    key: 'disk',
    label: 'Disk',
    value: `${Math.round(diskFreeGb)} GB free`,
    short: 'Low disk',
    title: 'Where the repos and worktrees live',
    warning: diskFreeGb < DISK_WARN_GB
  }
}

/** "42% · resets in 3h": one Claude usage window, as the drawer's Health and the Runners list's Usage show it. */
export function usageWindowText(usage: FleetRunnerUsageWindow, now: number): string {
  return `${Math.round(usage.pct)}% · resets ${whenText(usage.resetsAt, now)}`
}

function usageItem(spec: UsageWindowSpec, usage: FleetRunnerUsageWindow | undefined, now: number): FleetRunnerHealthItem | null {
  if (!usage) return null
  return {
    key: spec.key,
    label: spec.label,
    value: usageWindowText(usage, now),
    short: `${spec.key === USAGE_5H.key ? '5h' : '7d'} usage ${Math.round(usage.pct)}%`,
    title: new Date(usage.resetsAt).toLocaleString(),
    warning: usage.pct >= spec.warnPct
  }
}
